import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import { Sparkle, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

/**
 * Dedicated Partner sign-in page (V2.0.73).
 *
 * Kept separate from /login because:
 *   • Partner-branded copy (earnings pitch, "no cold pitches") sets
 *     the right expectation before the form.
 *   • Auto-force-logout of stale sessions (candidate/recruiter/admin)
 *     so users navigating here from /partner never bounce into the
 *     wrong dashboard.
 *   • Non-partner accounts are politely rejected on submit with a
 *     link to the correct sign-in page instead of silently signing
 *     them in and redirecting.
 */
export default function PartnerLogin() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // Auto-signout any existing session so the form is always usable.
    useEffect(() => {
        if (!user) return;
        if (user.role === "partner") {
            nav("/partner/dashboard", { replace: true });
            return;
        }
        // Any non-partner role — drop their session so the form is clean.
        api.post("/auth/logout").catch(() => {});
        setUser(false);
    }, [user, setUser, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.role !== "partner") {
                const label = data.role === "candidate" ? "candidate" : "recruiter/admin";
                setErr(
                    `That account is a ${label} account, not a partner. Sign in on the ${label} page instead.`
                );
                try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
                return;
            }
            setUser(data);
            nav("/partner/dashboard", { replace: true });
        } catch (e) {
            setErr(describeAxiosError(e));
        } finally {
            setLoading(false);
        }
    };

    const bounced = searchParams.get("reason");

    return (
        <div className="min-h-screen w-full flex bg-[#FAFAFA]" data-testid="partner-login-page">
            {/* Form column */}
            <div className="w-full lg:w-3/5 flex items-center justify-center px-6 sm:px-12 py-10 bg-white border-r border-[#E5E5E5]">
                <div className="w-full max-w-md">
                    <div className="mb-8 flex items-center justify-between gap-4">
                        <Brand size="h-8" to="/partner" />
                        <Link
                            to="/partner"
                            data-testid="partner-login-back"
                            className="text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1"
                        >
                            <ArrowLeft size={12} weight="bold" /> Back
                        </Link>
                    </div>

                    <div
                        data-testid="partner-login-badge"
                        className="inline-flex items-center gap-2 mb-5 bg-[#0891B2] text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]"
                    >
                        <Sparkle size={11} weight="fill" /> Partner sign in
                    </div>

                    <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter mb-3">
                        Sign in to your <br />
                        <span className="text-[#0891B2]">partner desk.</span>
                    </h1>
                    <p className="text-sm text-[#525252] mb-2 max-w-sm">
                        Browse live requirements, submit candidates and track earnings.
                    </p>
                    <p className="text-xs text-[#A3A3A3] mb-8">
                        Not a partner yet?{" "}
                        <Link
                            data-testid="partner-login-to-signup"
                            to="/partner"
                            className="text-[#0891B2] font-bold underline underline-offset-2 hover:text-[#0e7490]"
                        >
                            Apply for early access →
                        </Link>
                    </p>

                    {bounced && (
                        <div data-testid="partner-login-bounce"
                             className="border border-[#0891B2]/25 bg-[#0891B2]/5 text-ink text-sm px-4 py-3 mb-4">
                            {bounced === "idle" && "You were signed out after inactivity. Please sign in again."}
                            {bounced === "new_device" && "You signed in on another device. Please sign in again to continue here."}
                            {!["idle", "new_device"].includes(bounced) && "Your session ended. Please sign in again."}
                        </div>
                    )}

                    <form onSubmit={submit} className="space-y-4" data-testid="partner-login-form">
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                Email
                            </label>
                            <input
                                required
                                type="email"
                                data-testid="partner-login-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full border border-[#E5E5E5] px-3.5 py-3 text-sm focus:outline-none focus:border-[#0891B2]"
                                placeholder="you@yourcompany.com"
                                autoComplete="email"
                                autoFocus
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">
                                    Password
                                </label>
                                <Link
                                    to="/forgot-password"
                                    data-testid="partner-login-forgot"
                                    className="text-[10px] font-bold uppercase tracking-wider text-[#0891B2] hover:text-[#0e7490]"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                            <input
                                required
                                type="password"
                                data-testid="partner-login-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full border border-[#E5E5E5] px-3.5 py-3 text-sm focus:outline-none focus:border-[#0891B2]"
                                placeholder="Password from your activation wizard"
                                autoComplete="current-password"
                            />
                        </div>

                        {err && (
                            <div data-testid="partner-login-error"
                                 className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="partner-login-submit"
                            className="w-full bg-[#0891B2] hover:bg-[#0e7490] text-white py-3 text-xs font-bold uppercase tracking-wider disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-colors"
                        >
                            {loading ? "Signing in…" : (<>Sign in <ArrowRight size={13} weight="bold" /></>)}
                        </button>
                    </form>

                    <p className="text-[10px] text-[#A3A3A3] mt-6 leading-relaxed">
                        Recruiter or admin?{" "}
                        <Link to="/login" className="text-[#0891B2] font-bold underline underline-offset-2">
                            Use the hiring-desk sign in →
                        </Link>
                    </p>
                </div>
            </div>

            {/* Right column — partner value prop (hidden on mobile) */}
            <aside className="hidden lg:flex w-2/5 flex-col justify-center px-16 bg-[#0A0A0A] text-white relative overflow-hidden">
                <div className="relative z-10 max-w-md">
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#0891B2] mb-3">
                        Partner Program
                    </p>
                    <h2 className="font-heading text-4xl font-black tracking-tighter mb-4">
                        Earn ₹25,000 – ₹5,00,000 per hire.
                    </h2>
                    <p className="text-white/70 text-sm leading-relaxed mb-6">
                        First-submitter-wins ownership across every live requirement on
                        NaukriDaddy. GST invoices generated for you. Payouts land in
                        your bank the moment a candidate is placed.
                    </p>
                    <ul className="space-y-2 text-sm text-white/80">
                        <li>· Cross-agency live requirements</li>
                        <li>· CV attachments &amp; auto-preview thumbnails</li>
                        <li>· Real-time status from recruiter Kanban</li>
                        <li>· Zero cold pitches, zero client chasing</li>
                    </ul>
                </div>
            </aside>
        </div>
    );
}
